import { sizeValueFor } from 'snippets/product/size-selector'

const FIELD_CLASS = 'w-full min-w-0 border border-neutrals-medium-grey p-2 paragraph-xs'

/**
 * A size option's label: the number in the shopper's unit, plus the width when the product has
 * one, since width is no longer shown on its own summary line.
 */
const sizeOptionLabel = (variant, sizeType) => {
  const size = sizeValueFor(variant, sizeType)
  const withWidth = variant.width ? `${size} · ${variant.width}` : size
  return variant.available ? withWidth : `${withWidth} — Sold out`
}

/**
 * Summary of the product being saved. Size binds to the active list's draft entry, so each list
 * keeps its own selection.
 *
 * Size is a dropdown rather than the PDP's size grid: the drawer is a summary, and a full grid
 * pushed the list rows below the fold. Width rides along in each option label.
 *
 * Quantity is not shown. A wishlist entry is always saved with a quantity of 1, so a field that
 * could only ever read "1" was answering a question nobody asked.
 */
export const WishlistProductCard = ({
  title,
  colour,
  image,
  badge,
  badgeBg,
  badgeFg,
  variants,
  activeEntry,
  activeListId,
  displayVariantId,
  productPrice,
  productId,
  sizeType,
  sizeLayout,
  isReadOnly,
  onSize
}) => {
  // A signed-out shopper has no draft, so fall back to whatever the PDP has selected.
  const shownVariantId = activeEntry?.variantId ?? displayVariantId
  const variant = variants.find((candidate) => String(candidate.id) === String(shownVariantId))

  return (
    <div class="rounded bg-primary-white" data-role="wishlist-drawer-card">
      <div class="flex flex-row gap-4 p-4">
        {image && (
          <img
            src={image}
            alt={title}
            loading="lazy"
            class="h-[120px] w-25 shrink-0 object-cover object-bottom"
          />
        )}

        <div class="flex min-w-0 flex-1 flex-col gap-1">
          {badge && (
            /* Colours come from the badge metaobject via the PDP's own render, so the drawer
               shows the same badge in the same colours rather than a hardcoded green. */
            <span
              class={`w-fit px-2 py-1 paragraph-xs font-bold ${badgeBg ? 'bg-[--wl-badge-bg]' : 'bg-secondary-neon'} ${
                badgeFg ? 'text-[--wl-badge-fg]' : ''
              }`}
              style={{ '--wl-badge-bg': badgeBg || '', '--wl-badge-fg': badgeFg || '' }}
              data-role="wishlist-drawer-badge"
            >
              {badge}
            </span>
          )}

          <div class="flex flex-row items-start justify-between gap-2">
            <h5 class="min-w-0 font-bold">{title}</h5>
            <div class="shrink-0 text-right">
              <div class={variant?.compareAtPrice ? 'font-bold text-primary-sale' : 'font-bold'}>{variant?.price ?? productPrice}</div>
              {variant?.compareAtPrice && (
                <div class="paragraph-sm text-neutrals-tertiary-grey line-through">{variant.compareAtPrice}</div>
              )}
            </div>
          </div>

          {colour && (
            <div class="paragraph-xs text-neutrals-secondary-grey" data-role="wishlist-drawer-colour">
              Colour: {colour}
            </div>
          )}

          <div class="mt-2 flex flex-row gap-2">
            {isReadOnly ? (
              <div class="flex flex-1 flex-col p-2 font-bold paragraph-xs" data-role="wishlist-drawer-size-readonly">
                <span>{variant ? `Size: ${sizeOptionLabel(variant, sizeType)}` : 'Not selected'}</span>
              </div>
            ) : (
              <label class="flex min-w-0 flex-1 flex-col paragraph-xs">
                <span>Size</span>
                <select
                  class={FIELD_CLASS}
                  data-role="wishlist-drawer-size"
                  value={shownVariantId ? String(shownVariantId) : ''}
                  disabled={!activeEntry}
                  onChange={(event) => onSize(event.currentTarget.value)}
                >
                  <option value="" disabled>
                    Select a size
                  </option>
                  {variants.map((option) => (
                    <option key={option.id} value={String(option.id)} disabled={!option.available}>
                      {sizeOptionLabel(option, sizeType)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}
